import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

export interface AiChatMessage {
  id: string
  role: 'user' | 'assistant'
  content: string
  createdAt: string
}

export interface AiApprovalPrompt {
  id: number
  conversationId: string | null
  tool: string
  arguments: Record<string, unknown> | null
  reason: string | null
}

export interface AiState {
  drawerOpen: boolean
  activeConversationId: string | null
  messages: AiChatMessage[]
  /** Id of the assistant message currently receiving streamed deltas. */
  streamingMessageId: string | null
  /** Conversation whose history has already been merged into `messages`. */
  hydratedConversationId: string | null
  isStreaming: boolean
  streamError: string | null
  historyPanelOpen: boolean
  approvalPrompt: AiApprovalPrompt | null
}

const initialState: AiState = {
  drawerOpen: false,
  activeConversationId: null,
  messages: [],
  streamingMessageId: null,
  hydratedConversationId: null,
  isStreaming: false,
  streamError: null,
  historyPanelOpen: false,
  approvalPrompt: null,
}

const aiSlice = createSlice({
  name: 'ai',
  initialState,
  reducers: {
    openDrawer(state) {
      state.drawerOpen = true
    },
    closeDrawer(state) {
      state.drawerOpen = false
    },
    toggleDrawer(state) {
      state.drawerOpen = !state.drawerOpen
    },
    setHistoryPanelOpen(state, action: PayloadAction<boolean>) {
      state.historyPanelOpen = action.payload
    },
    setActiveConversation(state, action: PayloadAction<string | null>) {
      state.activeConversationId = action.payload
      state.messages = []
      state.streamingMessageId = null
      state.hydratedConversationId = null
      state.streamError = null
      // An approval prompt carries a conversationId: leaving the previous
      // conversation's prompt up would ask the user to approve a tool call
      // against a conversation they are no longer looking at.
      state.approvalPrompt = null
    },
    hydrateMessages(state, action: PayloadAction<{ conversationId: string; messages: Array<{ id: string; role: string; content: string; createdAt?: string }> }>) {
      if (state.activeConversationId !== action.payload.conversationId) return
      if (state.hydratedConversationId === action.payload.conversationId) return
      const hydrated = action.payload.messages
        .filter((message) => message.role === 'user' || message.role === 'assistant')
        .map((message) => ({
          id: message.id,
          role: message.role as 'user' | 'assistant',
          content: message.content ?? '',
          createdAt: message.createdAt ?? new Date().toISOString(),
        }))
      const hydratedIds = new Set(hydrated.map((message) => message.id))
      // Merge instead of discarding: messages the client already pushed (e.g.
      // a send dispatched before the history resolved) stay after the fetched
      // history rather than silently blocking the whole hydration.
      state.messages = [
        ...hydrated,
        ...state.messages.filter((message) => !hydratedIds.has(message.id)),
      ]
      state.hydratedConversationId = action.payload.conversationId
    },
    appendUserMessage(state, action: PayloadAction<AiChatMessage>) {
      state.messages.push(action.payload)
    },
    startAssistantMessage(state, action: PayloadAction<string>) {
      state.isStreaming = true
      state.streamError = null
      state.streamingMessageId = action.payload
      state.messages.push({
        id: action.payload,
        role: 'assistant',
        content: '',
        createdAt: new Date().toISOString(),
      })
    },
    appendDelta(state, action: PayloadAction<string>) {
      if (!state.isStreaming || !state.streamingMessageId) return
      // Scope the delta to the message that is actually streaming — applying
      // it to "whatever happens to be last" corrupted reordered/extra entries.
      const streaming = state.messages.find(
        (message) => message.id === state.streamingMessageId,
      )
      if (!streaming || streaming.role !== 'assistant') return
      streaming.content += action.payload
    },
    finishAssistantMessage(state) {
      state.isStreaming = false
      state.streamingMessageId = null
      // Mirror setStreamError: a successful finish after a failed stream (or a
      // late delta error) must not leave the previous streamError on screen.
      state.streamError = null
    },
    setStreamError(state, action: PayloadAction<string | null>) {
      state.isStreaming = false
      state.streamingMessageId = null
      state.streamError = action.payload
    },
    clearMessages(state) {
      state.messages = []
      state.activeConversationId = null
      state.streamingMessageId = null
      state.hydratedConversationId = null
      state.streamError = null
      state.approvalPrompt = null
    },
    setApprovalPrompt(state, action: PayloadAction<AiApprovalPrompt | null>) {
      state.approvalPrompt = action.payload
    },
  },
})

export const {
  openDrawer,
  closeDrawer,
  toggleDrawer,
  setHistoryPanelOpen,
  setActiveConversation,
  hydrateMessages,
  appendUserMessage,
  startAssistantMessage,
  appendDelta,
  finishAssistantMessage,
  setStreamError,
  clearMessages,
  setApprovalPrompt,
} = aiSlice.actions

export default aiSlice.reducer
